// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// One machine at a time (M9-32, 11 §4).
//
// THE CONFUSION THIS REMOVES
// --------------------------
// The Builder's draft was React state and nothing else. It died on a reload, it could only be felt
// inside a framed canvas in a scrolling editor, and there was no relationship at all between "the
// machine I am building" and "the machine I play" — so an author held two machines in their head
// and the product had an opinion about neither.
//
// So there is **one draft per browser**, it survives a reload, and the front door plays either it
// or the flagship — never something else, never ambiguously. `activeMachineFor` is the whole
// decision, in one pure function, because every caller needs it and it is easy to get subtly wrong.
//
// A DRAFT IS NOT A PUBLISHED MACHINE, AND THREE THINGS FOLLOW
// -----------------------------------------------------------
// **It cannot be scored.** 04 §1 makes a published version immutable and a leaderboard score a
// claim about specific bytes: the server re-simulates the player's input trace against them. A
// draft has no such version and changes under the player between one game and the next, so a game
// played on one goes nowhere — not to a board, and not onto this browser's own table either. That
// last part is not fussiness: a draft opened from the flagship carries `machineId: neon-nova` until
// its author publishes, so its scores would land on NEON NOVA's local table under NEON NOVA's name.
// `scored` is how a host is told, and `MachineStage` acts on it by being handed **no history and no
// outbox at all** rather than by remembering not to write.
//
// **It reaches the front door from this browser only.** FR-030 and NFR-006 make `/` a page that
// performs no network I/O whatsoever, and `scripts/check-anonymous.mjs` is what says so
// mechanically. This module therefore imports the two local stores and the core's validator, and
// nothing that could open a connection.
//
// **What comes back is bytes from an older build until proved otherwise.** The Builder writes
// whatever definition it was holding, and the format has a version. So a stored draft is validated
// on the way out and dropped if it no longer loads — see `readDraft`.
//
// AND IT APPLIES TO THE FRONT DOOR ONLY
// -------------------------------------
// `/play/:machineId` is untouched, deliberately. `machineSource.ts` has said since M3-37 why a
// machine that cannot be served must never quietly become something else: a shared link would
// produce a different game for the person who receives it. A link names a machine; only `/` has an
// active one.

import { validateMachine } from '@core/machine'
import { openDocuments, type DocumentStore } from '../storage/db'
import { readLocal, removeLocal, safeStorage, writeLocal, type StorageLike } from '../storage/local'

/** Where the one draft lives in the document store. */
export const DRAFT_KEY = 'builder/draft'

/** Where the choice lives. `localStorage`, because the front door needs it before it paints. */
export const ACTIVE_KEY = 'hcc.pinball.active'

/** What the front door is playing. */
export type ActiveChoice = 'flagship' | 'draft'

/** A draft, as it comes back off the disk. */
export interface StoredDraft {
  /** Validated on read. Handed to `MachineStage` as-is, the way `machineSource` hands one over. */
  readonly definition: unknown
  /** What the author called it, for the control that says which machine is on screen. */
  readonly title: string
  /** When it was last written, so a future "last edited" line has something to say. */
  readonly savedAt: number
}

// --- the draft ------------------------------------------------------------------------------

/**
 * The document store, opened once.
 *
 * Memoised rather than opened per call: `openDocuments` is asynchronous, answers `undefined`
 * instead of throwing, and every caller here would otherwise pay for a fresh `indexedDB.open`.
 * A caller that passes its own store — every test, and the Builder's own save loop — never
 * reaches this.
 */
let opening: Promise<DocumentStore | undefined> | undefined
function documents(): Promise<DocumentStore | undefined> {
  opening ??= openDocuments()
  return opening
}

/** `undefined` means "ask the browser"; `null` is never passed, so the default can be lazy. */
type Store = DocumentStore | undefined
const resolved = async (store: Store | 'ask'): Promise<Store> =>
  store === 'ask' ? await documents() : store

/**
 * The draft, if there is one this build can still run.
 *
 * **Validated, and dropped when it fails.** `machineSource.ts` refuses an unreadable machine with a
 * named error carrying the class that refused it, because somebody *asked* for that machine and a
 * link has to say why it did not open. This is the opposite case: nobody asked, these are simply
 * the bytes that were here last time, and the honest answer is that there is no draft. Removing
 * them as well is what stops the same failure being re-validated on every visit for ever.
 */
export async function readDraft(store: Store | 'ask' = 'ask'): Promise<StoredDraft | undefined> {
  const disk = await resolved(store)
  if (disk === undefined) return undefined
  const held = await disk.get<Partial<StoredDraft>>(DRAFT_KEY)
  if (held === undefined || held.definition === undefined) return undefined
  if (!validateMachine(held.definition).ok) {
    await disk.remove(DRAFT_KEY)
    return undefined
  }
  return {
    definition: held.definition,
    title: typeof held.title === 'string' ? held.title : '',
    savedAt: typeof held.savedAt === 'number' ? held.savedAt : 0,
  }
}

/**
 * Write the draft, replacing whatever was there.
 *
 * One key, because the whole point is that there is no second machine to be confused with. Says
 * whether it stuck, and never throws: a draft that could not be saved is a small loss and a page
 * that will not render because it could not save one is not — `local.ts`'s rule, unchanged.
 */
export async function saveDraft(
  definition: unknown,
  store: Store | 'ask' = 'ask',
): Promise<boolean> {
  const disk = await resolved(store)
  if (disk === undefined) return false
  const meta = (definition as { meta?: { title?: unknown } }).meta
  const title = typeof meta?.title === 'string' ? meta.title.trim() : ''
  return disk.put(DRAFT_KEY, { definition, title, savedAt: Date.now() } satisfies StoredDraft)
}

/** Throw the draft away. The second half of "reset to the default machine". */
export async function discardDraft(store: Store | 'ask' = 'ask'): Promise<void> {
  const disk = await resolved(store)
  await disk?.remove(DRAFT_KEY)
}

// --- the choice -----------------------------------------------------------------------------

type Listener = (choice: ActiveChoice) => void
const listeners = new Set<Listener>()

/**
 * What this browser last chose, or the flagship.
 *
 * **Anything unrecognised reads as the flagship**, which is the same direction every default in
 * this product errs in: the front door has to work for somebody who has never opened the Builder,
 * and a value written by an older build is exactly that person.
 */
export function readActiveChoice(storage: StorageLike | undefined = safeStorage()): ActiveChoice {
  return readLocal(storage, ACTIVE_KEY) === 'draft' ? 'draft' : 'flagship'
}

/**
 * Choose, and tell whoever is watching.
 *
 * Idempotent and silent when nothing changed — `playFocus`'s contract, and for its reason: callers
 * set this from effects that re-run. The flagship removes the key rather than writing a word for
 * it, so a browser that has never chosen and a browser that has chosen the default are the same
 * browser, which is what makes "reset to default" leave nothing behind.
 *
 * **"Changed" is asked of the store, not of a variable this module keeps.** A module-level `last`
 * was the first version and it is wrong twice: it starts as nothing, so the first write of
 * `flagship` on a fresh page announces a change that did not happen, and it is shared by every
 * caller in the process — which showed up immediately as one test in a file silencing the next.
 * The store is the fact; a cached copy of it is a second opinion.
 */
export function writeActiveChoice(
  choice: ActiveChoice,
  storage: StorageLike | undefined = safeStorage(),
): void {
  const changed = readActiveChoice(storage) !== choice
  if (choice === 'draft') writeLocal(storage, ACTIVE_KEY, 'draft')
  else removeLocal(storage, ACTIVE_KEY)
  if (!changed) return
  for (const listener of listeners) listener(choice)
}

/** Watch it, so the stage and the Builder cannot disagree. Returns the unsubscribe. */
export function watchActiveChoice(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// --- the decision ---------------------------------------------------------------------------

/** What the front door should mount, and what it is allowed to do with the result. */
export interface ActiveMachine {
  /** What it settled on — which is not always what was asked for. */
  readonly choice: ActiveChoice
  /** The definition to run. */
  readonly definition: unknown
  /** May a game on it be written down at all? False for a draft. See the file header. */
  readonly scored: boolean
  /** Is there a second machine to offer, and therefore a control to draw? */
  readonly switchable: boolean
  /**
   * What the draft calls itself, exactly — **which is often nothing, and often the flagship's own
   * name.**
   *
   * A draft opened from NEON NOVA carries NEON NOVA's title until its author changes it, and the
   * Builder does not require one until publish. Neither is fixed here: this module has no message
   * bundle, so a word invented in it would be an English string on a French page, and *"what do we
   * call a machine with no name"* is a question about a sentence rather than about storage. The
   * switch decides — see `ActiveMachineSwitch`, which suppresses a title that would name both
   * machines the same thing.
   */
  readonly draftTitle: string
}

/**
 * The whole decision.
 *
 * **A choice pointing at nothing is the flagship**, and that is the case worth having a function
 * for: the choice lives in `localStorage` and the draft lives in IndexedDB, so they are two stores
 * that can disagree — a draft discarded in one tab leaves the other tab's choice behind it, and
 * two visits later a browser can be set to `draft` with nothing under it. A stage handed
 * `undefined` is a blank screen with no error anywhere, which is the worst shape this could take.
 *
 * The flagship is **passed in** rather than read here, so this module knows nothing about which
 * machine is the default and cannot become a second opinion about it — and so the same function is
 * honest about a route that is showing something else entirely.
 */
export function activeMachineFor(
  choice: ActiveChoice,
  draft: StoredDraft | undefined,
  flagship: unknown,
): ActiveMachine {
  const draftTitle = draft === undefined ? '' : draft.title.trim()
  if (choice === 'draft' && draft !== undefined) {
    return { choice: 'draft', definition: draft.definition, scored: false, switchable: true, draftTitle }
  }
  return {
    choice: 'flagship',
    definition: flagship,
    scored: true,
    switchable: draft !== undefined,
    draftTitle,
  }
}

